import React, { useState, useRef, useEffect, useCallback } from 'react';
import { parseMarkdownToReact } from '../utils/markdownRenderer';
import { API_URL } from '../services/ApiService';
import './MarkdownFieldSimple.css';

/**
 * MarkdownFieldSimple - Field with simpler @mention highlighting using CSS
 */
const MarkdownFieldSimple = ({
  value = '',
  onChange,
  placeholder = '',
  disabled = false,
  type = 'input',
  className = '',
  rows = 3,
  isEditing: externalIsEditing,
  onBeginEdit,
  onEndEdit,
  ...props
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [validUsernames, setValidUsernames] = useState(new Set());
  const fieldRef = useRef(null);
  const validationCache = useRef(new Map());
  
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  
  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Validate usernames in the text
  const validateUsernames = useCallback(async (text) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[1]);
    }
    
    if (mentions.length === 0) {
      setValidUsernames(new Set());
      return;
    }
    
    const newMentions = mentions.filter(username => !validationCache.current.has(username));
    
    if (newMentions.length > 0) {
      try {
        const validationPromises = newMentions.map(async (username) => {
          const response = await fetch(
            `${API_URL}/users/search?q=${encodeURIComponent(username)}&limit=1`,
            { credentials: 'include' }
          );
          
          if (response.ok) {
            const users = await response.json();
            const isValid = users.some(user => 
              user.username?.toLowerCase() === username.toLowerCase()
            );
            validationCache.current.set(username, isValid);
            return { username, isValid };
          }
          return { username, isValid: false };
        });
        
        await Promise.all(validationPromises);
      } catch (err) {
        console.error('Error validating usernames:', err);
      }
    }
    
    const validSet = new Set();
    mentions.forEach(username => {
      if (validationCache.current.get(username)) {
        validSet.add(username);
      }
    });
    
    setValidUsernames(validSet);
  }, []);

  // Debounced validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue) {
        validateUsernames(localValue);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [localValue, validateUsernames]);
  
  // Search users for mentions dropdown
  useEffect(() => {
    const searchUsers = async () => {
      if (mentionSearch.length < 1) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/users/search?q=${encodeURIComponent(mentionSearch)}&limit=10`,
          { credentials: 'include' }
        );
        
        if (response.ok) {
          const users = await response.json();
          setSuggestions(users || []);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [mentionSearch]);
  
  const handleBeginEdit = () => {
    if (disabled) return;
    
    setLocalValue(value || '');
    
    if (externalIsEditing === undefined) {
      setInternalIsEditing(true);
    }
    if (onBeginEdit) {
      onBeginEdit();
    }
  };
  
  const handleEndEdit = () => {
    setShowMentions(false);
    setMentionStartIndex(-1);
    setMentionSearch('');
    
    if (externalIsEditing === undefined) {
      setInternalIsEditing(false);
    }
    if (onEndEdit) {
      onEndEdit();
    }
  };
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Check for @mentions
    const prevChar = cursorPos > 1 ? newValue[cursorPos - 2] : null;
    if (newValue[cursorPos - 1] === '@' && (cursorPos === 1 || [' ', '\n', '\t'].includes(prevChar))) {
      setMentionStartIndex(cursorPos - 1);
      setMentionSearch('');
      setShowMentions(true);
      setSelectedSuggestionIndex(0);
    } else if (mentionStartIndex >= 0) {
      const searchText = newValue.substring(mentionStartIndex + 1, cursorPos);
      
      if (searchText.includes(' ') || searchText.includes('\n') || !newValue.substring(mentionStartIndex).startsWith('@')) {
        setShowMentions(false);
        setMentionStartIndex(-1);
        setMentionSearch('');
      } else {
        setMentionSearch(searchText);
      }
    }
    
    setLocalValue(newValue);
    onChange(newValue);
  };
  
  const handleKeyDown = (e) => {
    // Handle Cmd+K for links
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      
      const start = fieldRef.current.selectionStart;
      const end = fieldRef.current.selectionEnd;
      const selectedText = localValue.substring(start, end) || 'link';
      
      const url = window.prompt(`Enter URL for "${selectedText}":`, 'https://');
      if (url) {
        const beforeSelection = localValue.substring(0, start);
        const afterSelection = localValue.substring(end);
        const markdownLink = `[${selectedText}](${url})`;
        const newValue = beforeSelection + markdownLink + afterSelection;
        
        setLocalValue(newValue);
        onChange(newValue);
        
        setTimeout(() => {
          if (fieldRef.current) {
            const newCursorPos = beforeSelection.length + markdownLink.length;
            fieldRef.current.focus();
            fieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
          }
        }, 0);
      }
      return;
    }
    
    // Handle mention suggestions navigation
    if (showMentions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => 
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => 
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
          
        case 'Enter':
        case 'Tab':
          if (showMentions && suggestions[selectedSuggestionIndex]) {
            e.preventDefault();
            insertMention(suggestions[selectedSuggestionIndex]);
          }
          break;
          
        case 'Escape':
          if (showMentions) {
            setShowMentions(false);
            setMentionStartIndex(-1);
            setMentionSearch('');
          } else {
            handleEndEdit();
          }
          break;
          
        default:
          break;
      }
    } else if (e.key === 'Escape') {
      handleEndEdit();
    } else if (e.key === 'Enter' && type === 'input') {
      e.preventDefault();
      handleEndEdit();
    }
  };
  
  const insertMention = (user) => {
    const beforeMention = localValue.substring(0, mentionStartIndex);
    const afterMention = localValue.substring(mentionStartIndex + mentionSearch.length + 1);
    const newValue = `${beforeMention}@${user.username} ${afterMention}`;
    
    setLocalValue(newValue);
    onChange(newValue);
    
    // Add to valid usernames immediately
    setValidUsernames(prev => new Set([...prev, user.username]));
    validationCache.current.set(user.username, true);
    
    // Reset mention state
    setShowMentions(false);
    setMentionStartIndex(-1);
    setMentionSearch('');
    setSelectedSuggestionIndex(0);
    
    // Focus back on field
    if (fieldRef.current) {
      const newCursorPos = beforeMention.length + user.username.length + 2;
      fieldRef.current.focus();
      fieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }
  };
  
  // Apply CSS class based on validation state
  const getFieldClassName = () => {
    const baseClass = type === 'textarea' ? 'markdown-field-textarea' : 'markdown-field-input';
    const validClass = validUsernames.size > 0 ? 'has-valid-mentions' : '';
    return `${className || baseClass} ${validClass}`;
  };
  
  // Create data attributes for CSS styling
  const getDataAttributes = () => {
    const attrs = {};
    validUsernames.forEach(username => {
      attrs[`data-mention-${username.toLowerCase()}`] = 'valid';
    });
    return attrs;
  };
  
  if (isEditing) {
    // Edit mode - simple input with CSS-based highlighting
    return (
      <div className="markdown-field-container">
        {type === 'textarea' ? (
          <textarea
            ref={fieldRef}
            className={getFieldClassName()}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleEndEdit}
            disabled={disabled}
            rows={rows}
            autoFocus
            {...getDataAttributes()}
            {...props}
          />
        ) : (
          <input
            ref={fieldRef}
            className={getFieldClassName()}
            placeholder={placeholder}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleEndEdit}
            disabled={disabled}
            autoFocus
            {...getDataAttributes()}
            {...props}
          />
        )}
        
        {/* Mention suggestions dropdown */}
        {showMentions && suggestions.length > 0 && (
          <div className="mention-suggestions-dropdown">
            {suggestions.map((user, index) => (
              <div
                key={user._id}
                className={`mention-suggestion-item ${
                  index === selectedSuggestionIndex ? 'selected' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(user);
                }}
              >
                <div className="mention-suggestion-avatar">
                  {(user.username?.[0] || user.email?.[0] || '?').toUpperCase()}
                </div>
                <div className="mention-suggestion-info">
                  <div className="mention-suggestion-username">@{user.username || user.email}</div>
                  {(user.firstName || user.lastName) && (
                    <div className="mention-suggestion-name">
                      {user.firstName} {user.lastName}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Visual indicator for valid mentions */}
        {validUsernames.size > 0 && (
          <div className="markdown-field-indicator">
            ✓ Valid mentions: {Array.from(validUsernames).map(u => `@${u}`).join(', ')}
          </div>
        )}
        
        {/* Help text */}
        <div className="markdown-field-help">
          Cmd+K for links • @ to mention • Markdown supported
        </div>
      </div>
    );
  }
  
  // Display mode - render markdown
  const renderedContent = parseMarkdownToReact(value || '', {
    renderMentions: true,
    renderLinks: true,
    renderBold: true,
    renderItalic: true,
    renderStrike: true,
    renderLineBreaks: type === 'textarea'
  });
  
  return (
    <div
      className={`${className || 'markdown-field-display'}`}
      onClick={handleBeginEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleBeginEdit();
        }
      }}
    >
      {renderedContent || <span className="markdown-field-placeholder">{placeholder}</span>}
    </div>
  );
};

export default MarkdownFieldSimple;